import { useState, useEffect } from "react";
import { TextField } from "./common/inputFiled";
import toast from "react-hot-toast";

export default function ChangePassword() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    old_password: "",
    new_password: "",
    repassword: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [apiError, setApiError] = useState("");

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setApiError("");
      try {
        const token = document.cookie.match(/admin_token=([^;]+)/)?.[1];
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/profile`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch profile");
        const json = await res.json();
        const data = json?.data?.[0];
        setProfile(data);
        setFormData((prev) => ({ ...prev, email: data?.email || "" }));
      } catch (e) {
        setApiError(e.message || "Failed to fetch profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setApiError("");
    // Validation
    const newErrors = {};
    if (!formData.old_password) newErrors.old_password = "Old password is required";
    if (!formData.new_password) newErrors.new_password = "New password is required";
    if (!formData.repassword) newErrors.repassword = "Please confirm new password";
    if (formData.new_password && formData.repassword && formData.new_password !== formData.repassword) {
      newErrors.repassword = "Passwords do not match";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Call change password API
    try {
      const token = document.cookie.match(/admin_token=([^;]+)/)?.[1];
      const body = new URLSearchParams();
      body.append("old_password", formData.old_password);
      body.append("new_password", formData.new_password);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: body.toString(),
      });
      const json = await res.json();
      if (!res.ok || json.status !== "1") throw new Error(json.message || "Failed to change password");
      toast.success("Password changed successfully");
      setErrors({});
      setFormData((prev) => ({ ...prev, old_password: "", new_password: "", repassword: "" }));
    } catch (e) {
      setApiError(e.message || "Failed to change password");
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F0FF] flex items-center justify-center px-4">
      <div className="w-full max-w-[520px] p-6 sm:p-8 md:p-10">
        {/* Profile Card */}
        {loading ? (
          <div className="mb-6 text-center text-gray-500">Loading profile...</div>
        ) : profile ? (
          <div className="mb-6 bg-gradient-to-br from-[#f5f4ff] to-[#e9e6fa] rounded-2xl shadow-lg p-6 flex items-center gap-5 border-[#7A5AF8] border-2">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-[#7A5AF8] flex items-center justify-center text-white text-2xl font-bold shadow-md">
                {profile.first_name?.[0] || ''}{profile.last_name?.[0] || ''}
              </div>
            </div>
            <div className="flex-1">
              <div className="font-bold text-xl text-[#47299c] mb-1 flex items-center gap-2">
                {profile.first_name} {profile.last_name}
                <span className="ml-2 px-2 py-0.5 text-xs rounded bg-[#e0d7fa] text-[#7A5AF8] font-semibold">Admin</span>
              </div>
              <div className="text-sm text-gray-700 mb-1 flex items-center gap-2">
                <svg className="w-4 h-4 text-[#7A5AF8]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 01-8 0m8 0a4 4 0 00-8 0m8 0V8a4 4 0 00-8 0v4m8 0v4a4 4 0 01-8 0v-4" /></svg>
                {profile.email}
              </div>
              <div className="text-xs text-gray-400 mt-1">Last login: {profile.last_login_at && new Date(profile.last_login_at).toLocaleString()}</div>
            </div>
          </div>
        ) : apiError ? (
          <div className="mb-6 text-red-500">{apiError}</div>
        ) : null}

        {/* Change Password Form */}
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField
            id="email"
            type="email"
            name="email"
            label="Email"
            placeholder="Email"
            value={formData.email}
            disabled
          />
          <TextField
            id="old_password"
            type="password"
            name="old_password"
            label="Old Password"
            placeholder="Enter old password"
            value={formData.old_password}
            onChange={handleChange}
            error={errors.old_password}
          />
          <TextField
            id="new_password"
            type="password"
            name="new_password"
            label="New Password"
            placeholder="Enter new password"
            value={formData.new_password}
            onChange={handleChange}
            error={errors.new_password}
          />
          <TextField
            id="repassword"
            type="password"
            name="repassword"
            label="Reconfirm Password"
            placeholder="Re-enter new password"
            value={formData.repassword}
            onChange={handleChange}
            error={errors.repassword}
          />
          {success && <div className="text-green-600 text-sm">{success}</div>}
          {apiError && <div className="text-red-500 text-sm">{apiError}</div>}
          <button
            type="submit"
            className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3.5 text-white font-semibold shadow-lg shadow-[#7C4DFF]/25 hover:brightness-105 active:scale-[0.99] transition"
          >
            Update
          </button>
        </form>
      </div>
    </div>
  );
}